import api from './api';
import {
  OdooSalesperson,
  Ejecutivo,
  EjecutivoConClientes,
  ClienteEjecutivo,
  EjecutivoStats,
  DashboardData,
  Pagination
} from '../types';

export interface CreateEjecutivoData {
  odoo_user_id: number;
  email?: string;
  name: string;
  password?: string;
}

export interface UpdateEjecutivoData {
  email?: string;
  username?: string;
  name?: string;
  is_active?: boolean;
}

class EjecutivoService {
  // ============================================
  // RUTAS PARA ADMIN (gestión de ejecutivos)
  // ============================================

  // Obtener vendedores de Odoo
  async getOdooSalespeople(search?: string): Promise<OdooSalesperson[]> {
    const response = await api.get('/ejecutivos/odoo/salespeople', {
      params: { search }
    });
    return response.data.data;
  }

  // Obtener ejecutivos que tienen clientes asignados en Odoo
  async getEjecutivosConClientes(search?: string): Promise<EjecutivoConClientes[]> {
    const response = await api.get('/ejecutivos/con-clientes', {
      params: { search }
    });
    return response.data.data;
  }

  // Obtener todos los ejecutivos
  async getAllEjecutivos(params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ ejecutivos: Ejecutivo[]; pagination: Pagination }> {
    const response = await api.get('/ejecutivos', { params });
    return response.data.data;
  }

  // Crear ejecutivo
  async createEjecutivo(data: CreateEjecutivoData): Promise<{ ejecutivo: Ejecutivo; tempPassword?: string }> {
    const response = await api.post('/ejecutivos', data);
    return response.data.data;
  }

  // Actualizar ejecutivo
  async updateEjecutivo(id: number, data: UpdateEjecutivoData): Promise<Ejecutivo> {
    const response = await api.put(`/ejecutivos/${id}`, data);
    return response.data.data;
  }

  // Cambiar contraseña de ejecutivo
  async changeEjecutivoPassword(id: number, newPassword: string, mustChangeOnLogin = true): Promise<void> {
    await api.post(`/ejecutivos/${id}/change-password`, {
      newPassword,
      mustChangeOnLogin
    });
  }

  // Ver clientes de un ejecutivo específico (admin)
  async getClientesByEjecutivo(
    ejecutivoId: number,
    params?: { search?: string; page?: number; limit?: number }
  ): Promise<{ clientes: ClienteEjecutivo[]; pagination: Pagination }> {
    const response = await api.get(`/ejecutivos/${ejecutivoId}/clientes`, { params });
    return response.data.data;
  }

  // Cambiar ejecutivo asignado a un cliente
  async cambiarEjecutivoCliente(partnerId: number, nuevoEjecutivoId: number): Promise<{ nuevoEjecutivo: string }> {
    const response = await api.post(`/ejecutivos/cliente/${partnerId}/cambiar-ejecutivo`, {
      nuevoEjecutivoId
    });
    return response.data.data;
  }

  // ============================================
  // RUTAS PARA EJECUTIVOS (su propio panel)
  // ============================================

  // Obtener estadísticas del ejecutivo actual
  async getMisEstadisticas(): Promise<EjecutivoStats> {
    const response = await api.get('/ejecutivos/mis-estadisticas');
    return response.data.data;
  }

  // Obtener mis clientes asignados
  async getMisClientes(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ clientes: ClienteEjecutivo[]; pagination: Pagination }> {
    const response = await api.get('/ejecutivos/mis-clientes', { params });
    return response.data.data;
  }

  // Ver dashboard de un cliente específico
  async getClienteDashboard(
    partnerId: number,
    options?: { startDate?: string; endDate?: string }
  ): Promise<DashboardData> {
    const response = await api.get(`/ejecutivos/cliente/${partnerId}/dashboard`, {
      params: options
    });
    return response.data.data;
  }

  // Resetear contraseña de un cliente
  async resetClientePassword(partnerId: number, newPassword?: string): Promise<{ tempPassword?: string }> {
    const response = await api.post(`/ejecutivos/cliente/${partnerId}/reset-password`, {
      newPassword
    });
    return response.data.data;
  }

  // Descargar PDF de estado de cuenta de un cliente
  async downloadClientePDF(partnerId: number, options?: { startDate?: string; endDate?: string }): Promise<void> {
    const response = await api.get(`/ejecutivos/cliente/${partnerId}/pdf`, {
      params: options,
      responseType: 'blob'
    });

    // Crear URL del blob y descargar
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Extraer nombre del archivo del header si existe
    const contentDisposition = response.headers['content-disposition'];
    let filename = `estado_cuenta_${new Date().toISOString().split('T')[0]}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Enviar estado de cuenta de un cliente por email
  async sendClienteStatementByEmail(
    partnerId: number,
    options?: { email?: string; startDate?: string; endDate?: string }
  ): Promise<{ success: boolean; message: string; sentTo: string }> {
    const response = await api.post(`/ejecutivos/cliente/${partnerId}/send-email`, options);
    return response.data;
  }
}

const ejecutivoService = new EjecutivoService();
export default ejecutivoService;
